import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { DeviceMobile, Desktop, DownloadSimple, Copy, Check, AppleLogo, AndroidLogo } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const DOWNLOAD_BASE = "https://mpdownloads7503.blob.core.windows.net/downloads";
const DOWNLOADS = [
  { label: "Windows (.exe)", file: "MarketPilot365-Setup.exe" },
  { label: "macOS (.dmg)", file: "MarketPilot365.dmg" },
  { label: "Linux (.AppImage)", file: "MarketPilot365.AppImage" },
];

export default function Install() {
  const url = typeof window !== "undefined" ? window.location.origin : "";
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [copied, setCopied] = useState(false);

  const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)").matches;

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => { window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  const install = async () => {
    if (!deferred) { toast("Use your browser menu → 'Install app' / 'Add to Home Screen'"); return; }
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") toast.success("Installing MarketPilot AI…");
    setDeferred(null);
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Copy failed — long-press the link to copy"); }
  };

  return (
    <div className="max-w-4xl">
      <div className="label-xs">Testing · Mobile</div>
      <h1 className="mt-1 font-heading text-3xl font-bold tracking-tighter flex items-center gap-2"><DeviceMobile size={26} className="text-volt" />       Get the App</h1>
            <p className="mt-3 text-sm text-muted-foreground max-w-xl">MarketPilot AI is an installable app (PWA). Install it on your laptop/desktop from Chrome or Edge, or scan the code with your phone and add it to your home screen. It launches in its own window like a native app. No app store needed.</p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* QR */}
        <div className="border border-border bg-card p-6 flex flex-col items-center">
          <div className="label-xs mb-4 self-start">Scan to open on your phone</div>
          <div className="bg-white p-4 rounded-md" data-testid="install-qr">
            <QRCodeSVG value={url} size={200} bgColor="#ffffff" fgColor="#0A0A0A" level="M" includeMargin={false} />
          </div>
          <div className="mt-5 w-full">
            <div className="label-xs mb-2">App link</div>
            <div className="flex items-center gap-2">
              <a href={url} data-testid="install-link" className="flex-1 truncate mono text-xs border border-border bg-background px-3 py-2 rounded-sm text-volt hover:underline">{url}</a>
              <Button data-testid="copy-link-btn" onClick={copy} variant="outline" size="sm" className="rounded-sm border-border h-9 px-3">{copied ? <Check size={16} className="text-signal-green" /> : <Copy size={16} />}</Button>
            </div>
          </div>
        </div>

        {/* Install */}
        <div className="border border-border bg-card p-6">
          <div className="label-xs mb-4">Install on this device</div>
          {standalone || installed ? (
            <div className="flex items-center gap-2 text-signal-green mono text-sm"><Check size={18} weight="bold" /> App is installed & running.</div>
          ) : (
            <>
              <Button data-testid="install-app-btn" onClick={install} disabled={isIOS}
                className="w-full bg-volt hover:bg-volt/90 text-white rounded-sm h-11 gap-2 font-medium">
                <DownloadSimple size={18} weight="bold" /> {deferred ? "Install App" : isIOS ? "Use Safari steps below" : "Install App"}
              </Button>
              {!deferred && !isIOS && <p className="mt-2 mono text-[11px] text-muted-foreground">If the button doesn't prompt, open your browser menu and choose "Install app" / "Add to Home Screen".</p>}
            </>
          )}

          <div className="mt-6 space-y-4">
            <div className="border border-border p-4 rounded-sm" data-testid="download-desktop">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium"><Desktop size={18} className="text-volt" /> Download the desktop app</div>
              <div className="flex flex-wrap gap-2">
                {DOWNLOADS.map((d) => (
                  <a key={d.file} href={`${DOWNLOAD_BASE}/${d.file}`} download
                    className="mono text-xs border border-border bg-background px-3 py-2 rounded-sm text-volt hover:underline">{d.label}</a>
                ))}
              </div>
              <p className="mt-2 mono text-[11px] text-muted-foreground">Installers aren't code-signed yet. Windows may show "Windows protected your PC": click More info → Run anyway. On Mac, right-click the app → Open.</p>
            </div>
            <div className="border border-border p-4 rounded-sm" data-testid="install-desktop">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium"><Desktop size={18} className="text-volt" /> Windows / Mac / Linux (Chrome or Edge)</div>
              <ol className="mono text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Open https://marketpilot365.com in Chrome or Edge</li>
                <li>Click the install icon at the right of the address bar, or ⋮ menu → "Install MarketPilot" / Apps → "Install this site as an app"</li>
                <li>Confirm — it opens in its own window and gets a Start menu / Dock shortcut</li>
              </ol>
            </div>
            <div className="border border-border p-4 rounded-sm">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium"><AndroidLogo size={18} className="text-signal-green" /> Android (Chrome)</div>
              <ol className="mono text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Open the link above in Chrome</li>
                <li>Tap ⋮ menu → "Install app" / "Add to Home screen"</li>
                <li>Confirm — the icon lands on your home screen</li>
              </ol>
            </div>
            <div className="border border-border p-4 rounded-sm">
              <div className="flex items-center gap-2 mb-2 text-sm font-medium"><AppleLogo size={18} /> iPhone / iPad (Safari)</div>
              <ol className="mono text-xs text-muted-foreground space-y-1 list-decimal list-inside">
                <li>Open the link above in Safari</li>
                <li>Tap the Share button (□↑)</li>
                <li>Choose "Add to Home Screen" → Add</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 mono text-[11px] text-muted-foreground">Note: for testing, the app runs from this preview URL. Data (watchlist, paper trades) syncs to the same account you log into on any device.</p>
    </div>
  );
}
